import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/axios';
import Header from '../components/Header';

const AdminBlog = () => {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    publicados: 0,
    rascunhos: 0,
    visualizacoes: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page,
        limit: 20,
        status: status === 'todos' ? '' : status,
        busca: searchTerm,
      };

      const { data } = await api.get('/api/blog/admin-artigos', { params });

      setArticles(data.artigos || []);
      setStats({
        total: data.stats?.total || 0,
        publicados: data.stats?.publicados || 0,
        rascunhos: data.stats?.rascunhos || 0,
        visualizacoes: data.stats?.visualizacoes || 0,
      });
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Erro ao buscar artigos:', err);
      setError('Erro ao carregar artigos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [page, status, searchTerm]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleDelete = async (id) => {
    if (deleteConfirm !== id) {
      setDeleteConfirm(id);
      return;
    }

    try {
      await api.delete(`/api/blog/artigos/${id}`);
      setArticles(articles.filter(article => article.id !== id));
      setDeleteConfirm(null);
      fetchArticles();
    } catch (err) {
      console.error('Erro ao deletar artigo:', err);
      alert('Erro ao deletar artigo. Tente novamente.');
      setDeleteConfirm(null);
    }
  };

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const getStatusBadge = (articleStatus) => {
    if (articleStatus === 'publicado') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Publicado
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
        </svg>
        Rascunho
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const colors = {
      tecnologia: { bg: 'bg-blue-100', text: 'text-blue-800' },
      negocios: { bg: 'bg-purple-100', text: 'text-purple-800' },
      desenvolvimento: { bg: 'bg-green-100', text: 'text-green-800' },
      design: { bg: 'bg-pink-100', text: 'text-pink-800' },
      marketing: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
    };

    const style = colors[category?.toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-800' };

    return (
      <span className={`inline-flex px-2 py-1 rounded text-sm font-medium ${style.bg} ${style.text}`}>
        {category || 'Sem categoria'}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Artigos */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Artigos</p>
                <p className="text-3xl font-bold text-navy-900 mt-2">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5 3a2 2 0 012-2h6a2 2 0 012 2v2h2a2 2 0 012 2v10a2 2 0 01-2 2H3a2 2 0 01-2-2V7a2 2 0 012-2h2V3z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Publicados */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Publicados</p>
                <p className="text-3xl font-bold text-navy-900 mt-2">{stats.publicados}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          {/* Rascunhos */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Rascunhos</p>
                <p className="text-3xl font-bold text-navy-900 mt-2">{stats.rascunhos}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Visualizações */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Visualizações</p>
                <p className="text-3xl font-bold text-navy-900 mt-2">{stats.visualizacoes.toLocaleString('pt-BR')}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M12.586 4.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM12.586 4.586l2.828 2.828M9.172 9.172L6.343 12M6.343 6.343l2.829 2.829m7.072-7.072l2.828 2.828M15.75 13.75L12 10m0 0l-2.828 2.829m2.828-2.829l2.828-2.828M9.172 9.172L6.343 12m7.071 0l2.829 2.829" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200">
              <button
                onClick={() => handleStatusChange('todos')}
                className={`px-4 py-2 font-medium transition-colors ${
                  status === 'todos'
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => handleStatusChange('publicado')}
                className={`px-4 py-2 font-medium transition-colors ${
                  status === 'publicado'
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Publicados
              </button>
              <button
                onClick={() => handleStatusChange('rascunho')}
                className={`px-4 py-2 font-medium transition-colors ${
                  status === 'rascunho'
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Rascunhos
              </button>
            </div>

            {/* Search and Button */}
            <div className="flex gap-4 flex-col sm:flex-row">
              <input
                type="text"
                placeholder="Buscar artigos..."
                value={searchTerm}
                onChange={handleSearch}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <Link
                to="/admin/blog/novo"
                className="px-6 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap"
              >
                + Novo Artigo
              </Link>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <div className="inline-block">
              <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-600 mt-4">Carregando artigos...</p>
          </div>
        )}

        {/* Articles Table - Desktop */}
        {!loading && articles.length > 0 && (
          <>
            <div className="hidden md:block bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-navy-900">Título</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-navy-900">Categoria</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-navy-900">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-navy-900">Visualizações</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-navy-900">Data</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-navy-900">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {articles.map((article) => (
                    <tr key={article.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-navy-900 line-clamp-2">{article.titulo}</p>
                      </td>
                      <td className="px-6 py-4">
                        {getCategoryBadge(article.categoria)}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(article.status)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">{article.visualizacoes || 0}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">{formatDate(article.criado_em)}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Link
                            to={`/admin/blog/${article.id}/editar`}
                            className="px-3 py-1 text-sm font-medium text-orange-600 hover:bg-orange-50 rounded transition-colors"
                          >
                            Editar
                          </Link>
                          <button
                            onClick={() => handleDelete(article.id)}
                            className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                              deleteConfirm === article.id
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                          >
                            {deleteConfirm === article.id ? 'Confirmar' : 'Deletar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Articles Cards - Mobile */}
            <div className="md:hidden space-y-4">
              {articles.map((article) => (
                <div key={article.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-navy-900 mb-2 line-clamp-2">{article.titulo}</h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Categoria:</span>
                      {getCategoryBadge(article.categoria)}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Status:</span>
                      {getStatusBadge(article.status)}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Visualizações:</span>
                      <span className="text-sm text-gray-900">{article.visualizacoes || 0}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Data:</span>
                      <span className="text-sm text-gray-900">{formatDate(article.criado_em)}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Link
                      to={`/admin/blog/${article.id}/editar`}
                      className="flex-1 px-3 py-2 text-sm font-medium text-center text-orange-600 hover:bg-orange-50 rounded transition-colors border border-orange-200"
                    >
                      Editar
                    </Link>
                    <button
                      onClick={() => handleDelete(article.id)}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded transition-colors border ${
                        deleteConfirm === article.id
                          ? 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200'
                          : 'text-red-600 border-red-200 hover:bg-red-50'
                      }`}
                    >
                      {deleteConfirm === article.id ? 'Confirmar' : 'Deletar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Empty State */}
        {!loading && articles.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-navy-900 mb-2">Nenhum artigo encontrado</h3>
            <p className="text-gray-600 mb-6">Comece criando seu primeiro artigo para o blog.</p>
            <Link
              to="/admin/blog/novo"
              className="inline-block px-6 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
            >
              + Novo Artigo
            </Link>
          </div>
        )}

        {/* Pagination */}
        {!loading && articles.length > 0 && totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Anterior
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  page === p
                    ? 'bg-orange-600 text-white'
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {p}
              </button>
            ))}

            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Próxima
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBlog;
